import { useEffect, useRef } from "react";

/**
 * Returns a ref to be passed to an html element. That element's height will be
 * saved to a css variable and updated when resized.
 *
 * ```
 * const { ref } = useDynamicHeight("dynamic-header-height");
 * ```
 *
 * It can then be used in css
 * ```
 * padding-top: var(--dynamic-header-height);
 * ```
 */
export function useDynamicHeight(cssVar: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        `--${cssVar}`,
        `${el.offsetHeight}px`,
      );
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [cssVar]);
  return { ref };
}
